/**
 * JoinGameOverlay.tsx — Join-game form (task H03).
 *
 * The invite code is the only public handle for a game; the server looks it
 * up, enforces Pending-only for new members, and is idempotent for existing
 * members (both outcomes return the safe game detail). On success the game
 * is opened immediately — a duplicate join simply re-opens the same game.
 *
 * Errors (unknown code 404, Started/Ended 409) are the server's messages,
 * shown verbatim. Dialog behavior (focus trap, Escape, focus return, ARIA)
 * comes from the shared K01 Modal.
 */
import { useRef, useState, type FormEvent } from 'react';
import { ApiClientError, type GameDetail } from '@shared';
import { apiV1 } from '../api';
import { Modal } from './Modal';

interface JoinGameOverlayProps {
  onClose: () => void;
  /** Called with the safe game detail (new join OR already a member). */
  onJoined: (game: GameDetail) => void;
}

export function JoinGameOverlay({ onClose, onJoined }: JoinGameOverlayProps) {
  const [inviteCode, setInviteCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // K02: synchronous duplicate-submit guard (state can't stop a same-tick
  // second click; the ref can).
  const submittingRef = useRef(false);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (submittingRef.current) return;
    const code = inviteCode.trim();
    if (code === '') {
      setError('Invite code is required.');
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const game = await apiV1.joinGame({ inviteCode: code });
      onJoined(game);
      // Success: the caller closes the overlay; clear the guard so a later
      // reopened overlay (same mounted instance) can submit again.
      submittingRef.current = false;
    } catch (err) {
      // Safe, non-secret API messages (unknown code, game not joinable, …).
      setError(err instanceof ApiClientError ? err.message : 'Could not join the game. Try again.');
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  return (
    <Modal title="Join a game" onClose={onClose} blocked={submitting}>
        <form className="overlay-form" onSubmit={(e) => void handleSubmit(e)} noValidate>
          <label>
            Invite code
            <input
              type="text"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value)}
              placeholder="29ecdc7e-a056-40ca-be4e-1ee790904e23"
              autoComplete="off"
              spellCheck={false}
              maxLength={64}
              disabled={submitting}
            />
          </label>

          {error && (
            <p className="auth-error" role="alert">
              {error}
            </p>
          )}

          <div className="overlay-actions">
            <button type="button" className="game-refresh" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="auth-submit" disabled={submitting}>
              {submitting ? 'Joining…' : 'Join game'}
            </button>
          </div>
        </form>
    </Modal>
  );
}
